import { ChevronDown, Search } from 'lucide-react';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  formatNationalDisplay,
  getPhoneCountry,
  searchPhoneCountries,
  stripNationalDigits,
  type PhoneCountry,
} from '../../utils/phoneCountries';

type InternationalPhoneInputProps = {
  id?: string;
  countryIso2: string;
  nationalDigits: string;
  onCountryChange: (iso2: string) => void;
  onNationalChange: (digits: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  prefilled?: boolean;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
};

const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
  id,
  countryIso2,
  nationalDigits,
  onCountryChange,
  onNationalChange,
  disabled = false,
  invalid = false,
  prefilled = false,
  label,
  hint,
  className = '',
}) => {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const country = getPhoneCountry(countryIso2);

  const results = useMemo(
    () => searchPhoneCountries(query, 80),
    [query],
  );

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleNationalChange = (value: string, nextCountry: PhoneCountry = country) => {
    onNationalChange(stripNationalDigits(value, nextCountry.nationalLength));
  };

  const selectCountry = (next: PhoneCountry) => {
    onCountryChange(next.iso2);
    onNationalChange(stripNationalDigits(nationalDigits, next.nationalLength));
    setQuery('');
    setOpen(false);
  };

  const inputClasses = [
    'intl-phone-input__number',
    'credit-checkout-input',
    'touch-manipulation',
    prefilled ? 'credit-checkout-input--prefilled' : '',
    invalid ? 'credit-checkout-input--missing' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`intl-phone-input ${className}`.trim()} ref={rootRef}>
      {label ? <div className="intl-phone-input__label">{label}</div> : null}
      <div className={`intl-phone-input__row${invalid ? ' intl-phone-input__row--invalid' : ''}`}>
        <div className="intl-phone-input__country-wrap">
          <button
            type="button"
            id={`${id || listId}-country`}
            className="intl-phone-input__country-btn touch-manipulation"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            disabled={disabled}
            onClick={() => setOpen((value) => !value)}
          >
            <span className="intl-phone-input__flag" aria-hidden>{country.flag}</span>
            <span className="intl-phone-input__dial">{country.dialCode}</span>
            <ChevronDown className="intl-phone-input__chevron" aria-hidden />
          </button>
          {open && (
            <div id={listId} className="intl-phone-input__menu" role="listbox">
              <div className="intl-phone-input__search">
                <Search className="intl-phone-input__search-icon" aria-hidden />
                <input
                  type="text"
                  value={query}
                  placeholder="Search country or +code…"
                  className="intl-phone-input__search-input"
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <ul className="intl-phone-input__list">
                {results.map((item) => (
                  <li key={item.iso2} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={item.iso2 === country.iso2}
                      className={`intl-phone-input__option${
                        item.iso2 === country.iso2 ? ' intl-phone-input__option--active' : ''
                      }`}
                      onMouseDown={(e) => { e.preventDefault(); selectCountry(item); }}
                    >
                      <span className="intl-phone-input__option-flag" aria-hidden>{item.flag}</span>
                      <span className="intl-phone-input__option-name">{item.name}</span>
                      <span className="intl-phone-input__option-dial">{item.dialCode}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <input
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          disabled={disabled}
          value={formatNationalDisplay(nationalDigits, country)}
          placeholder={country.placeholder}
          className={inputClasses}
          onChange={(e) => handleNationalChange(e.target.value)}
        />
      </div>
      {hint ? <p className="credit-checkout-field-hint">{hint}</p> : null}
    </div>
  );
};

export default InternationalPhoneInput;
